/**
 * In-app browser: what it may do without asking, and what it keeps.
 *
 * @module
 */
import { DomainList } from "./DomainList";
import { ensureEnvironmentApi } from "../../environmentApi";
import { getPrimaryEnvironmentConnection } from "../../environments/runtime";
import { useSettings, useUpdateSettings } from "../../hooks/useSettings";
import { ensureLocalApi } from "../../localApi";
import { useServerConfig } from "../../rpc/serverState";
import { Button } from "../ui/button";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { toastManager } from "../ui/toast";
import { SettingResetButton, SettingsRow, SettingsSection } from "./settingsLayout";
import { searchableSetting } from "./settingsSearch";
import { DEFAULT_UNIFIED_SETTINGS } from "@cmmd-forge/contracts/settings";
import { BanIcon, PanelTopIcon, ShieldCheckIcon } from "lucide-react";
import { useCallback, useState } from "react";

const BROWSER_APPROVAL_LABELS = {
  alwaysAsk: "Navigate without asking",
  askUnknown: "Only allowed domains",
  neverAsk: "Navigate without asking",
} as const;

const BROWSER_ANNOTATION_LABELS = {
  always: "Always include",
  ask: "Ask first",
  never: "Never include",
} as const;

const BROWSER_SESSION_RETENTION_LABELS = {
  closeOnThreadEnd: "Close on thread end",
  keepForHandoff: "Keep for handoff",
} as const;

export function BrowserSection() {
  const settings = useSettings();
  const serverConfig = useServerConfig();
  const { updateSettings } = useUpdateSettings();
  const [isClearingBrowsingData, setIsClearingBrowsingData] = useState(false);
  const browserSettings = settings.browser;
  const updateBrowserSettings = (patch: Partial<typeof browserSettings>) =>
    updateSettings({ browser: { ...browserSettings, ...patch } });
  const clearBrowsingData = useCallback(async () => {
    const confirmed = await ensureLocalApi().dialogs.confirm(
      "Clear all in-app browser data?\nThis closes active browser sessions and deletes saved captures for this environment.",
    );
    if (!confirmed) return;
    const environmentId =
      serverConfig?.environment.environmentId ?? getPrimaryEnvironmentConnection().environmentId;
    if (!environmentId) {
      toastManager.add({
        type: "error",
        title: "Browser data was not cleared",
        description: "The active backend environment is not ready yet.",
      });
      return;
    }
    setIsClearingBrowsingData(true);
    try {
      const browserApi = (() => {
        try {
          return ensureEnvironmentApi(environmentId).browser;
        } catch {
          return getPrimaryEnvironmentConnection().client.browser;
        }
      })();
      if (!browserApi?.clearBrowsingData) {
        throw new Error("Browser controller is not available in this environment.");
      }
      const result = await browserApi.clearBrowsingData({ environmentId });
      toastManager.add({
        type: "success",
        title: "Browser data cleared",
        description: `Closed ${result.closedSessionCount} session(s), deleted ${result.deletedSessionCount} saved session(s), and removed ${result.deletedCaptureCount} capture(s).`,
      });
    } catch (error) {
      toastManager.add({
        type: "error",
        title: "Browser data was not cleared",
        description: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsClearingBrowsingData(false);
    }
  }, [serverConfig?.environment.environmentId]);

  return (
    <>
      <SettingsSection id="browser" title="Browser" icon={<PanelTopIcon className="size-3.5" />}>
        <SettingsRow
          {...searchableSetting("browser-use")}
          description="Let Forge agents control the built-in browser through the canonical browser controller."
          resetAction={
            browserSettings.enabled !== DEFAULT_UNIFIED_SETTINGS.browser.enabled ? (
              <SettingResetButton
                label="browser use"
                onClick={() =>
                  updateBrowserSettings({ enabled: DEFAULT_UNIFIED_SETTINGS.browser.enabled })
                }
              />
            ) : null
          }
          control={
            <Switch
              checked={browserSettings.enabled}
              onCheckedChange={(checked) => updateBrowserSettings({ enabled: Boolean(checked) })}
              aria-label="Enable browser use"
            />
          }
        />
      </SettingsSection>

      <SettingsSection title="Data">
        <SettingsRow
          {...searchableSetting("browser-browsing-data")}
          description="Clear server-scoped in-app browser sessions, captures, cookies, cache, and site state for this environment."
          control={
            <Button
              variant="outline"
              disabled={isClearingBrowsingData}
              onClick={() => void clearBrowsingData()}
            >
              {isClearingBrowsingData ? "Clearing..." : "Clear browsing data"}
            </Button>
          }
        />
        <SettingsRow
          {...searchableSetting("browser-annotation-screenshots")}
          description="Screenshots help agents understand and address browser comments, but increase context usage."
          control={
            <Select
              value={browserSettings.annotationScreenshots}
              onValueChange={(value) => {
                if (value === "always" || value === "ask" || value === "never") {
                  updateBrowserSettings({ annotationScreenshots: value });
                }
              }}
            >
              <SelectTrigger className="w-full sm:w-44" aria-label="Annotation screenshots">
                <SelectValue>
                  {BROWSER_ANNOTATION_LABELS[browserSettings.annotationScreenshots]}
                </SelectValue>
              </SelectTrigger>
              <SelectPopup align="end" alignItemWithTrigger={false}>
                <SelectItem hideIndicator value="always">
                  {BROWSER_ANNOTATION_LABELS.always}
                </SelectItem>
                <SelectItem hideIndicator value="ask">
                  {BROWSER_ANNOTATION_LABELS.ask}
                </SelectItem>
                <SelectItem hideIndicator value="never">
                  {BROWSER_ANNOTATION_LABELS.never}
                </SelectItem>
              </SelectPopup>
            </Select>
          }
        />
        <SettingsRow
          {...searchableSetting("browser-session-retention")}
          description="Choose what happens to browser sessions when an agent thread ends."
          control={
            <Select
              value={browserSettings.sessionRetention}
              onValueChange={(value) => {
                if (value === "closeOnThreadEnd" || value === "keepForHandoff") {
                  updateBrowserSettings({ sessionRetention: value });
                }
              }}
            >
              <SelectTrigger className="w-full sm:w-44" aria-label="Browser session retention">
                <SelectValue>
                  {BROWSER_SESSION_RETENTION_LABELS[browserSettings.sessionRetention]}
                </SelectValue>
              </SelectTrigger>
              <SelectPopup align="end" alignItemWithTrigger={false}>
                <SelectItem hideIndicator value="keepForHandoff">
                  {BROWSER_SESSION_RETENTION_LABELS.keepForHandoff}
                </SelectItem>
                <SelectItem hideIndicator value="closeOnThreadEnd">
                  {BROWSER_SESSION_RETENTION_LABELS.closeOnThreadEnd}
                </SelectItem>
              </SelectPopup>
            </Select>
          }
        />
      </SettingsSection>

      <SettingsSection title="Permissions" icon={<ShieldCheckIcon className="size-3.5" />}>
        <SettingsRow
          {...searchableSetting("browser-approval")}
          description="Choose whether Forge can open websites directly in the built-in browser."
          control={
            <Select
              value={browserSettings.approvalMode === "askUnknown" ? "askUnknown" : "neverAsk"}
              onValueChange={(value) => {
                if (value === "askUnknown" || value === "neverAsk") {
                  updateBrowserSettings({ approvalMode: value });
                }
              }}
            >
              <SelectTrigger className="w-full sm:w-52" aria-label="Browser approval mode">
                <SelectValue>{BROWSER_APPROVAL_LABELS[browserSettings.approvalMode]}</SelectValue>
              </SelectTrigger>
              <SelectPopup align="end" alignItemWithTrigger={false}>
                <SelectItem hideIndicator value="askUnknown">
                  {BROWSER_APPROVAL_LABELS.askUnknown}
                </SelectItem>
                <SelectItem hideIndicator value="neverAsk">
                  {BROWSER_APPROVAL_LABELS.neverAsk}
                </SelectItem>
              </SelectPopup>
            </Select>
          }
        />
      </SettingsSection>

      <SettingsSection title="Blocked domains" icon={<BanIcon className="size-3.5" />}>
        <SettingsRow
          {...searchableSetting("browser-never-open")}
          description="Forge will block these domains for the built-in browser."
        >
          <DomainList
            emptyLabel="No blocked domains"
            domains={browserSettings.blockedDomains}
            onChange={(blockedDomains) => updateBrowserSettings({ blockedDomains })}
          />
        </SettingsRow>
      </SettingsSection>

      <SettingsSection title="Allowed domains" icon={<ShieldCheckIcon className="size-3.5" />}>
        <SettingsRow
          {...searchableSetting("browser-open-without-asking")}
          description="Domains listed here can open without approval when approval mode allows it."
        >
          <DomainList
            emptyLabel="No allowed domains"
            domains={browserSettings.allowedDomains}
            onChange={(allowedDomains) => updateBrowserSettings({ allowedDomains })}
          />
        </SettingsRow>
      </SettingsSection>
    </>
  );
}

/**
 * Archived work: Forge threads and CMMD tasks, on every surface.
 *
 * This panel used to fork on `isElectron` and show web users only archived
 * CMMD tasks. That dropped archived Forge *threads* — the ones the Activity
 * sidebar parks in its Archived shelf — from the browser entirely, and with
 * them the only `unarchiveThread` control in the app, so archiving a thread on
 * web was a one-way trip. The two lifecycles are independent, so both render
 * here and the empty state only appears when neither has anything.
 */
